import { type ReactElement } from 'react';
import { DataTableGrid } from './DataTableGrid';
import { buildServerDataGridStateParams } from './grid/serverDataGridStateParams';
import { buildServerDataTableGridProps } from './serverDataTableGridProps';
import { useServerDataTableGridModel } from './useServerDataTableGridModel';
import type { BaseDataTableProps, ServerDataTableProps } from './dataTableShared';
import type { GridValidRowModel } from '@/ui/primitives';

export const DataTableServer = <T extends GridValidRowModel>({
  title,
  actions,
  state = 'ready',
  emptyTitle,
  emptyBody,
  errorTitle,
  errorBody,
  errorAction,
  server,
}: BaseDataTableProps & {
  server: ServerDataTableProps<T>;
}): ReactElement => {
  const {
    queryPath,
    queryFn,
    columns,
    getRowId,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel,
    initialFilterModel,
    queryKeyPrefix,
    queryKey,
    onRefetch,
    onInvalidate,
    onRowClick,
    onCellClick,
    onColumnHeaderClick,
    onQueryInput,
    isAuthenticated = true,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    disableColumnMenu = true,
    showToolbar = false,
    interactionMode = 'interactive',
    getRowHeight,
    columnVisibilityModel,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    gridProps: serverGridProps,
  } = server;
  const stateParams = buildServerDataGridStateParams<T>({
    queryPath,
    queryFn,
    columns,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel,
    initialFilterModel,
    queryKeyPrefix,
    queryKey,
    onRefetch,
    onInvalidate,
    onQueryInput,
    isAuthenticated,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    columnVisibilityModel,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    getRowHeight,
  });

  const {
    errorMessage,
    rowsData,
    rowCount,
    queryIsFetching,
    resolvedPageSizeOptions,
    resolvedNoRowsText,
    paginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    handleFilterModelChange,
    columnVisibilityModel: visibilityModel,
    setColumnVisibilityModel,
    handleColumnWidthChange,
    columnsWithWidths,
    suppressFocusOutline,
  } = useServerDataTableGridModel<T>({
    stateParams,
    columns,
    interactionMode,
    onColumnHeaderClick,
  });
  const resolvedGridProps = buildServerDataTableGridProps<T>({
    autoHeight: autoHeight ?? true,
    captureScrollAnchor,
    columnVisibilityModel: visibilityModel,
    columns: columnsWithWidths,
    disableColumnMenu,
    effectiveErrorMessage: errorMessage ?? errorTitle ?? null,
    effectiveRowCount: rowCount,
    effectiveRowsData: rowsData,
    filterModel,
    getRowHeight,
    getRowId,
    gridProps: serverGridProps,
    handleColumnWidthChange,
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    onCellClick,
    onColumnHeaderClick,
    onRowClick,
    paginationModel,
    queryIsFetching,
    resolvedNoRowsText: emptyTitle ?? resolvedNoRowsText,
    resolvedPageSizeOptions,
    setColumnVisibilityModel,
    showToolbar,
    sortModel,
    suppressFocusOutline,
  });

  return (
    <DataTableGrid
      title={title}
      actions={actions}
      state={state}
      emptyTitle={emptyTitle}
      emptyBody={emptyBody}
      errorTitle={errorTitle}
      errorBody={errorBody}
      errorAction={errorAction}
      gridProps={resolvedGridProps}
    />
  );
};
